
import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const units = [
  { id: 'w', name: 'Watts (W)' },
  { id: 'kw', name: 'Kilowatts (kW)' },
  { id: 'mw', name: 'Megawatts (MW)' },
  { id: 'gw', name: 'Gigawatts (GW)' },
  { id: 'hp', name: 'Horsepower (hp)' },
  { id: 'hp_uk', name: 'Horsepower (UK)' },
  { id: 'ps', name: 'Metric Horsepower (PS)' },
  { id: 'btu_h', name: 'BTU per hour' },
  { id: 'ftlb_s', name: 'Foot-pound per second' },
  { id: 'cal_s', name: 'Calories per second' },
  { id: 'btu_s', name: 'BTU per second' },
  { id: 'va', name: 'Volt-Ampere (VA)' },
  { id: 'dbm', name: 'Decibel-milliwatts (dBm)' },
];

// Conversion rates to Watts
const conversionRates: Record<string, number> = {
  'w': 1,
  'kw': 1000,
  'mw': 1000000,
  'gw': 1000000000,
  'hp': 745.7,
  'hp_uk': 745.7,
  'ps': 735.49875,
  'btu_h': 0.29307107,
  'ftlb_s': 1.355818,
  'cal_s': 4.184,
  'btu_s': 1055.06,
  'va': 1,  // Assuming power factor of 1
  'dbm': 0.001, // 1 mW = 0 dBm, uses logarithmic scale but we'll use 0.001 as approximation
};

const PowerConverter = () => {
  const [fromValue, setFromValue] = useState<string>('1');
  const [fromUnit, setFromUnit] = useState<string>('kw');
  const [toUnit, setToUnit] = useState<string>('hp');
  const [toValue, setToValue] = useState<string>('');

  useEffect(() => {
    convert();
  }, [fromValue, fromUnit, toUnit]);

  const convert = () => {
    try {
      const inputValue = parseFloat(fromValue);
      if (!isNaN(inputValue)) {
        // Special case for dBm which is logarithmic
        if (fromUnit === 'dbm' && toUnit !== 'dbm') {
          const mW = Math.pow(10, inputValue / 10);
          const watts = mW / 1000;
          const result = watts / conversionRates[toUnit];
          setToValue(result.toFixed(10).replace(/\.?0+$/, ""));
        } else if (fromUnit !== 'dbm' && toUnit === 'dbm') {
          // Converting to dBm
          const watts = inputValue * conversionRates[fromUnit];
          const mW = watts * 1000;
          const result = 10 * Math.log10(mW);
          setToValue(result.toFixed(10).replace(/\.?0+$/, ""));
        } else {
          // Regular conversion
          const baseValue = inputValue * conversionRates[fromUnit];
          const result = baseValue / conversionRates[toUnit];
          setToValue(result.toFixed(10).replace(/\.?0+$/, ""));
        }
      } else {
        setToValue('');
      }
    } catch (error) {
      console.error('Conversion error:', error);
      setToValue('Error');
    }
  };

  const handleFromValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFromValue(e.target.value);
  };

  const handleFromUnitChange = (value: string) => {
    setFromUnit(value);
  };

  const handleToUnitChange = (value: string) => {
    setToUnit(value);
  };

  const swapUnits = () => {
    setFromUnit(toUnit);
    setToUnit(fromUnit);
    setFromValue(toValue);
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle>Power Converter</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">From:</label>
            <div className="grid grid-cols-3 gap-2">
              <Input 
                type="number" 
                value={fromValue} 
                onChange={handleFromValueChange} 
                className="col-span-2"
              />
              <Select value={fromUnit} onValueChange={handleFromUnitChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Unit" />
                </SelectTrigger>
                <SelectContent>
                  {units.map((unit) => (
                    <SelectItem key={unit.id} value={unit.id}>
                      {unit.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex justify-center">
            <button 
              onClick={swapUnits}
              className="p-2 rounded-full hover:bg-muted"
            >
              ⇅
            </button>
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">To:</label>
            <div className="grid grid-cols-3 gap-2">
              <Input 
                type="text" 
                value={toValue} 
                readOnly 
                className="col-span-2"
              />
              <Select value={toUnit} onValueChange={handleToUnitChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Unit" />
                </SelectTrigger>
                <SelectContent>
                  {units.map((unit) => (
                    <SelectItem key={unit.id} value={unit.id}>
                      {unit.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PowerConverter;
